// Phase 15 Plan 05 Task 3 — Net wrappers for the tournament surface.
//
// Endpoints:
//   - GET  /api/tournaments/{tournamentId}      → Tournament wire (incl. rounds[5])
//   - POST /api/tournaments/{code}/join         → join by 8-char invite code
//
// The "active tournament list" is NOT a separate endpoint — the lobby's
// GET /api/home payload includes `active_tournaments: TournamentPreview[]`
// (DEC-007). The helper `getActiveTournamentsFromLobby` reads that array
// from the `$lobbyData` store via a getter callback so this module stays
// free of store imports (avoid circular dependency on stores/lobbyData →
// net/home → stores/lobbyData).

import type { components } from '@runegambit/api-types'
import { apiFetch } from './client'
import { createIdempotencyKey } from './idempotency'

export type Tournament = components['schemas']['Tournament']
export type TournamentPreview = components['schemas']['TournamentPreview']
export type RoundPairing = components['schemas']['RoundPairing']

/**
 * GET /api/tournaments/{tournamentId} — full tournament wire.
 *
 * Carries standings, pairings_current_round (legacy back-compat), and the
 * Phase 15 additive `rounds[5]` Swiss-5 projection that drives the
 * R1..R5 tab strip (Plan 10's RoundTabStrip consumer).
 */
export async function fetchTournamentById(
  tournamentId: string,
): Promise<Tournament> {
  return apiFetch<Tournament>(`/api/tournaments/${tournamentId}`, 'GET')
}

/**
 * Response shape for POST /api/tournaments/{code}/join.
 * Server-side: server validates the 8-char code, dedupes the participants
 * row, returns the tournament_id on success.
 */
export interface JoinTournamentResponse {
  readonly tournament_id: string
}

/**
 * POST /api/tournaments/{code}/join — join by 8-char invite code.
 *
 * Mutating op → fresh Idempotency-Key per call. 5xx retries once with the
 * same key (FIFO-50 cache hit; idempotent join semantics).
 */
export async function joinTournament(
  code: string,
): Promise<JoinTournamentResponse> {
  return apiFetch<JoinTournamentResponse>(
    `/api/tournaments/${encodeURIComponent(code)}/join`,
    'POST',
    {
      idempotencyKey: createIdempotencyKey(),
    },
  )
}
